// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FulfillmentSearch } from '@/fulfillment/components/fulfillment-search';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

/**
 * Props for the FormFulfillmentSearch component.
 * Extends react-hook-form's controller props for form integration.
 */
interface Props<Values extends FieldValues> extends UseControllerProps<Values> {
  /**
   * The label text displayed above the input element.
   * Should clearly indicate what type of fulfillment to select.
   */
  label: string;

  /**
   * The react-hook-form control instance.
   * Used to integrate the component with the form's state management.
   */
  control: Control<Values>;

  /**
   * If true, filters out third-party fulfilled orders from the results.
   * Defaults to false.
   */
  ignore3pl?: boolean;

  /**
   * If true, the input field is disabled and cannot be interacted with.
   */
  disabled?: boolean;
}

/**
 * A controlled fulfillment search component using react-hook-form.
 *
 * Features:
 * - Full form state integration
 * - Validation state handling
 * - Error message display
 * - Controlled value management
 * - Optional 3PL filtering
 *
 * Form Integration:
 * - Uses react-hook-form's useController for state management
 * - Handles undefined values appropriately
 * - Preserves form validation states
 * - Supports field-level validation
 *
 * The component wraps FulfillmentSearch to provide seamless integration
 * with react-hook-form while maintaining all fulfillment search functionality.
 */
export function FormFulfillmentSearch<Values extends FieldValues>(
  props: Props<Values>
) {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  // Ensure consistent null value for the controlled input
  // when the field value is undefined
  const value = field.value ?? null;

  return (
    <FulfillmentSearch
      ref={field.ref}
      name={field.name}
      label={props.label}
      value={value}
      error={Boolean(fieldState.error)}
      onChange={field.onChange}
      ignore3pl={props.ignore3pl}
      disabled={props.disabled}
      helperText={fieldState.error?.message}
    />
  );
}
